import { useEffect, useMemo, useState } from 'react';
import catalog from './google-fonts-catalog.json';

type Font = { family: string; category: string; popularity: number; latin: boolean };
type Props = {
  label: string;
  value: string;
  sample: string;
  disabled: boolean;
  onChange: (family: string) => void;
};

const fonts = catalog as Font[];
const categories = ['All', 'Sans Serif', 'Serif', 'Display', 'Handwriting', 'Monospace'];

function loadFontStyles(families: string[], id: string) {
  const unique = [...new Set(families.filter(Boolean))];
  if (!unique.length) return;
  const params = new URLSearchParams();
  unique.forEach((family) => params.append('family', family));
  params.set('display', 'swap');
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?${params.toString()}`;
  document.head.appendChild(link);
  return () => link.remove();
}

export default function GoogleFontPicker({ label, value, sample, disabled, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState<'popular' | 'alphabetical'>('popular');
  const [visibleCount, setVisibleCount] = useState(24);
  const [preview, setPreview] = useState('The quick brown fox jumps over the lazy dog');

  const matches = useMemo(() => fonts
    .filter((font) => (category === 'All' || font.category === category) &&
      font.family.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => sort === 'alphabetical'
      ? a.family.localeCompare(b.family)
      : a.popularity - b.popularity), [category, query, sort]);
  const visible = matches.slice(0, visibleCount);

  useEffect(() => {
    if (!open) return;
    return loadFontStyles(visible.map((font) => font.family), `font-browser-${label.replace(/\W/g, '-')}`);
  }, [open, label, visibleCount, category, query, sort]);

  useEffect(() => loadFontStyles([value], `font-selected-${label.replace(/\W/g, '-')}`), [label, value]);

  useEffect(() => {
    if (!open) return;
    const onEscape = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [open]);

  const updateFilter = (nextQuery: string, nextCategory: string) => {
    setQuery(nextQuery);
    setCategory(nextCategory);
    setVisibleCount(24);
  };

  return <>
    <div>
      <p className="mb-1.5 text-xs font-semibold text-[#202223] dark:text-slate-300">{label}</p>
      <button type="button" disabled={disabled} onClick={() => setOpen(true)}
        className="w-full rounded-xl border border-[#D2D5D9] bg-white p-4 text-left transition hover:border-[#006838] hover:shadow-sm disabled:cursor-not-allowed disabled:opacity-70 dark:border-slate-700 dark:bg-slate-900">
        <span className="flex items-center justify-between gap-2 text-xs text-[#5C5F62] dark:text-slate-400">
          <span>{value || 'Choose a font'}</span><span>{disabled ? '' : 'Browse fonts →'}</span>
        </span>
        <span className="mt-3 block truncate text-xl text-[#202223] dark:text-white" style={{ fontFamily: `"${value}", sans-serif` }}>{sample}</span>
      </button>
    </div>
    {open && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3 sm:p-6" onMouseDown={(event) => { if (event.target === event.currentTarget) setOpen(false); }}>
      <div role="dialog" aria-modal="true" aria-label={`Choose ${label}`} className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-[#1A1D21]">
        <div className="border-b border-[#E1E4E8] p-5 dark:border-slate-800 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div><h3 className="text-xl font-semibold text-[#202223] dark:text-white">Choose {label.toLowerCase()}</h3>
              <p className="mt-1 text-xs text-[#5C5F62] dark:text-slate-400">Explore {fonts.length.toLocaleString()} Google Fonts. Select a card to preview it on your storefront.</p></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close font browser" className="rounded-lg px-3 py-1 text-xl text-[#5C5F62] hover:bg-slate-100 dark:hover:bg-slate-800">×</button>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
            <input type="search" value={query} onChange={(event) => updateFilter(event.target.value, category)} placeholder="Search font families"
              className="h-10 rounded-lg border border-[#D2D5D9] bg-white px-3 text-sm text-[#202223] outline-none focus:border-[#006838] dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
            <select value={sort} onChange={(event) => { setSort(event.target.value as 'popular' | 'alphabetical'); setVisibleCount(24); }} aria-label="Sort fonts"
              className="h-10 rounded-lg border border-[#D2D5D9] bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-white">
              <option value="popular">Most popular</option><option value="alphabetical">A to Z</option>
            </select>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {categories.map((item) => <button type="button" key={item} onClick={() => updateFilter(query, item)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs ${category === item ? 'bg-[#006838] text-white' : 'bg-[#F1F2F4] text-[#202223] dark:bg-slate-800 dark:text-slate-200'}`}>{item}</button>)}
          </div>
          <input value={preview} onChange={(event) => setPreview(event.target.value)} aria-label="Font preview text" placeholder="Type text to preview"
            className="mt-3 w-full border-b border-[#E1E4E8] bg-transparent py-2 text-sm text-[#202223] outline-none focus:border-[#006838] dark:border-slate-700 dark:text-white" />
        </div>
        <div className="overflow-y-auto p-5 sm:p-6">
          <p className="mb-4 text-xs text-[#5C5F62] dark:text-slate-400">{matches.length.toLocaleString()} fonts found</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {visible.map((font) => <button type="button" key={font.family} onClick={() => { onChange(font.family); setOpen(false); }}
              className={`min-h-32 rounded-xl border p-4 text-left transition hover:border-[#006838] hover:shadow-md ${value === font.family ? 'border-[#006838] bg-emerald-50 dark:bg-emerald-950/30' : 'border-[#D2D5D9] dark:border-slate-700'}`}>
              <span className="flex items-center justify-between gap-2 text-xs text-[#5C5F62] dark:text-slate-400"><span>{font.family}</span><span>{font.category}</span></span>
              <span className="mt-4 block overflow-hidden text-ellipsis whitespace-nowrap text-2xl text-[#202223] dark:text-white" style={{ fontFamily: `"${font.family}", sans-serif` }}>{preview || font.family}</span>
            </button>)}
          </div>
          {!matches.length && <p className="py-12 text-center text-sm text-[#5C5F62]">No fonts match your search.</p>}
          {visibleCount < matches.length && <button type="button" onClick={() => setVisibleCount((count) => count + 24)}
            className="mx-auto mt-6 block rounded-lg border border-[#D2D5D9] px-5 py-2 text-sm text-[#202223] hover:border-[#006838] dark:border-slate-700 dark:text-white">Show more fonts</button>}
        </div>
      </div>
    </div>}
  </>;
}
